import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { DownloadSimple, Trash, ArrowLeft, EnvelopeSimple, MagicWand, Spinner, ArrowsClockwise } from "@phosphor-icons/react";
import { DisclaimerBanner } from "../components/Disclaimer";
import { AIDisclaimer } from "../components/AIDisclaimer";

export default function QuoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sketchLoading, setSketchLoading] = useState({}); // photo_index -> bool
  const [sketchStyle, setSketchStyle] = useState("ponteggio");

  // Build map: photo_index -> sketch
  const sketchMap = {};
  (q?.sketches || []).forEach((s) => { sketchMap[s.photo_index] = s; });

  useEffect(() => {
    api.get(`/quotes/${id}`).then(({data})=>setQ(data)).catch(()=>toast.error("Non trovato"));
  }, [id]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const filename = `preventivo_${q?.number || id}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      // iOS Safari needs the anchor in the DOM to honor download
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free memory after the browser has had time to start the download
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("PDF scaricato");
    } catch { toast.error("Errore PDF"); }
  };

  const remove = async () => {
    if (!confirm("Eliminare il preventivo?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Eliminato");
    nav("/preventivi");
  };

  if (!q) return <Layout><div className="text-[#A3A3A3]">Caricamento…</div></Layout>;

  return (
    <Layout>
      <button onClick={()=>nav(-1)} className="text-[#A3A3A3] flex items-center gap-1 mb-4"><ArrowLeft size={18}/>Indietro</button>
      <DisclaimerBanner />
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="font-mono text-xs text-[#FFD600]">N° {q.number}</div>
          <h1 className="font-heading text-3xl font-black uppercase">{q.client_name || "—"}</h1>
          <div className="text-sm text-[#A3A3A3]">{q.site_address}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase text-[#A3A3A3] font-bold">Totale</div>
          <div className="font-heading text-3xl font-black text-[#FFD600]">€ {(q.total||0).toFixed(2)}</div>
        </div>
      </div>

      {q.description && (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
          <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2">Descrizione</div>
          <p className="text-sm whitespace-pre-wrap">{q.description}</p>
        </div>
      )}

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-3">Voci</div>
        <div className="space-y-2">
          {q.items.map((it) => (
            <div key={it.code + "-" + it.description} className="bg-[#0A0A0A] rounded p-3 flex justify-between gap-3">
              <div className="flex-1">
                <div className="font-mono text-xs text-[#FFD600]">{it.code}</div>
                <div className="text-sm font-bold">{it.description}</div>
                <div className="text-xs text-[#A3A3A3]">{it.quantity} {it.unit} × € {it.price}</div>
              </div>
              <div className="font-mono text-[#FFD600] font-bold">€ {(it.quantity*it.price).toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>

      {q.photos?.length > 0 && (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold">Foto · Overlay IA</div>
            <select data-testid="sketch-style" value={sketchStyle} onChange={(e)=>setSketchStyle(e.target.value)}
              className="bg-[#0A0A0A] border border-[#333] rounded h-8 px-2 text-xs text-white focus:border-[#FFD600] focus:outline-none">
              <option value="ponteggio">Ponteggio</option>
              <option value="demolizione">Demolizione/Spicconamento</option>
              <option value="finitura">Finitura/Risultato finale</option>
              <option value="impianto_elettrico">Impianto elettrico</option>
              <option value="impianto_idraulico">Impianto idraulico</option>
            </select>
          </div>
          <AIDisclaimer compact />
          <div className="grid grid-cols-2 gap-3">
            {q.photos.map((p, i) => {
              const sk = sketchMap[i];
              const loading = sketchLoading[i];
              const generate = async () => {
                setSketchLoading((s) => ({ ...s, [i]: true }));
                try {
                  await api.post(`/quotes/${id}/sketches`, { photo_index: i, style: sketchStyle });
                  // Reload quote to get fresh sketches
                  const { data } = await api.get(`/quotes/${id}`);
                  setQ(data);
                  toast.success("Overlay IA salvato nel preventivo");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore generazione");
                } finally {
                  setSketchLoading((s) => ({ ...s, [i]: false }));
                }
              };
              const removeSk = async () => {
                if (!confirm("Eliminare l'overlay IA?")) return;
                try {
                  await api.delete(`/quotes/${id}/sketches/${sk.id}`);
                  const { data } = await api.get(`/quotes/${id}`);
                  setQ(data);
                  toast.success("Overlay eliminato");
                } catch { toast.error("Errore eliminazione"); }
              };
              return (
                <div key={p._id || p.data.slice(0,80)} className="space-y-2">
                  <img src={p.data} alt="" className="w-full h-32 object-cover rounded border border-[#333]" />
                  {sk ? (
                    <>
                      <img src={`data:${sk.mime_type};base64,${sk.data}`} alt="sketch"
                        className="w-full h-32 object-cover rounded border-2 border-[#FFD600]" data-testid={`sketch-${i}`}/>
                      <div className="grid grid-cols-2 gap-1">
                        <button data-testid={`sketch-regen-${i}`} onClick={generate} disabled={loading}
                          className="bg-[#1A1A1A] border border-[#FFD600]/40 text-[#FFD600] h-9 rounded text-[10px] font-black uppercase flex items-center justify-center gap-1 disabled:opacity-50">
                          {loading ? <Spinner size={12} className="animate-spin"/> : <ArrowsClockwise size={12} weight="bold"/>}
                          {loading ? "Genero…" : "Rigenera"}
                        </button>
                        <button data-testid={`sketch-del-${i}`} onClick={removeSk}
                          className="bg-[#1A1A1A] border border-[#FF3B30]/40 text-[#FF3B30] h-9 rounded text-[10px] font-black uppercase flex items-center justify-center gap-1">
                          <Trash size={12} weight="bold"/> Elim.
                        </button>
                      </div>
                    </>
                  ) : (
                    <button data-testid={`sketch-btn-${i}`} onClick={generate} disabled={loading}
                      className="w-full bg-[#1A1A1A] border-2 border-[#FFD600]/40 text-[#FFD600] h-12 rounded-md font-black uppercase text-xs flex items-center justify-center gap-1 disabled:opacity-50">
                      {loading ? <><Spinner size={16} className="animate-spin"/> Genero…</> : <><MagicWand size={16} weight="bold"/> Overlay IA</>}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <button data-testid="pdf-btn" onClick={downloadPdf}
          className="bg-[#FFD600] text-[#0A0A0A] h-14 rounded-md font-black uppercase flex items-center justify-center gap-2">
          <DownloadSimple size={22} weight="bold"/> PDF
        </button>
        <button data-testid="email-btn" onClick={()=>setShowEmail(true)}
          className="bg-[#1A1A1A] border-2 border-[#FFD600]/40 text-[#FFD600] h-14 rounded-md font-black uppercase flex items-center justify-center gap-2">
          <EnvelopeSimple size={22} weight="bold"/> Email
        </button>
        <button data-testid="delete-btn" onClick={remove}
          className="bg-[#1A1A1A] border-2 border-[#FF3B30]/40 text-[#FF3B30] h-14 rounded-md font-black uppercase flex items-center justify-center gap-2">
          <Trash size={22} weight="bold"/> Elim.
        </button>
      </div>

      {showEmail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-2" onClick={()=>!sending && setShowEmail(false)}>
          <div className="bg-[#1A1A1A] border border-[#333] rounded-md w-full max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="border-b border-[#333] p-4 font-heading text-xl font-bold uppercase">Invia preventivo via email</div>
            <div className="p-4 space-y-3">
              <input data-testid="email-to" type="email" required value={emailTo} onChange={(e)=>setEmailTo(e.target.value)} placeholder="cliente@email.it"
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <textarea data-testid="email-msg" placeholder="Messaggio (opzionale)" value={emailMsg} onChange={(e)=>setEmailMsg(e.target.value)}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] rounded-md p-3 min-h-[80px] focus:border-[#FFD600] focus:outline-none"/>
              <div className="flex gap-2">
                <button onClick={()=>setShowEmail(false)} disabled={sending}
                  className="flex-1 bg-[#1A1A1A] border-2 border-[#333] h-12 rounded-md font-black uppercase">Annulla</button>
                <button data-testid="email-send" disabled={sending || !emailTo}
                  onClick={async()=>{
                    setSending(true);
                    try {
                      await api.post(`/quotes/${id}/email`, { to: emailTo, message: emailMsg });
                      toast.success("Email inviata!"); setShowEmail(false); setEmailTo(""); setEmailMsg("");
                    } catch(e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
                    finally { setSending(false); }
                  }}
                  className="flex-1 bg-[#FFD600] text-[#0A0A0A] h-12 rounded-md font-black uppercase disabled:opacity-50">
                  {sending ? "..." : "Invia"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
